/**
 * Centralized access control codes.
 *
 * These constants are the single source of truth for module, feature, and
 * permission identifiers used across the application — in nav items,
 * route guards, service-layer assertions, and inline UI checks.
 *
 * Good documentation on how to use the
 * enforcement layer found at docs/ACCESS_CONTROL_ENFORCEMENT.md
 *
 * Good documentation on how the RBAC is modeled found at docs/architecture/licensing and rbac.md
 *
 * Usage:
 *   import { Modules, Features, Permissions } from '@prolarus/shared';
 *
 *   await assertCanPerform(context, Modules.COMPLIANCE, Permissions.invoice.create.code);
 *   <RequireAccess module={Modules.COMPLIANCE} permission={Permissions.invoice.read.code}>
 */
// ---------------------------------------------------------------------------
// Modules
// ---------------------------------------------------------------------------
export const Modules = {
    /** Compliance Intelligence */
    COMPLIANCE: 'compliance',
    /** RFQ Management */
    QUOTING: 'quoting',
    /** AFE Management */
    AFE: 'afe',
};
// ---------------------------------------------------------------------------
// Features
// ---------------------------------------------------------------------------
export const Features = {
    /** Compliance Intelligence features */
    COMPLIANCE: {
        /** Vendor Report Card */
        VENDOR_REPORT_CARD: 'compliance.vendor_report_card',
        /** Early Pay functionality */
        EARLY_PAY: 'compliance.early_pay',
    },
};
// ---------------------------------------------------------------------------
// Permissions
// ---------------------------------------------------------------------------
export const Permissions = {
    invoice: {
        create: {
            code: 'invoice.create',
            displayName: 'Create Invoice',
            description: 'Allows user to create new invoices',
        },
        read: {
            code: 'invoice.read',
            displayName: 'Read Invoice',
            description: 'Allows user to view invoices',
        },
        update: {
            code: 'invoice.update',
            displayName: 'Update Invoice',
            description: 'Allows user to edit existing invoices',
        },
        delete: {
            code: 'invoice.delete',
            displayName: 'Delete Invoice',
            description: 'Allows user to delete invoices',
        },
        upload: {
            code: 'invoice.upload',
            displayName: 'Upload Invoice',
            description: 'Allows user to upload invoices',
        },
    },
    quote: {
        create: {
            code: 'quote.create',
            displayName: 'Create Quote',
            description: 'Allows user to create new quotes',
        },
        read: {
            code: 'quote.read',
            displayName: 'Read Quote',
            description: 'Allows user to view quotes',
        },
        update: {
            code: 'quote.update',
            displayName: 'Update Quote',
            description: 'Allows user to edit existing quotes',
        },
        delete: {
            code: 'quote.delete',
            displayName: 'Delete Quote',
            description: 'Allows user to delete quotes',
        },
    },
    api: {
        create: {
            code: 'api.create',
            displayName: 'Create API Key',
            description: 'Allows user to create new API keys',
        },
        read: {
            code: 'api.read',
            displayName: 'Read API Key',
            description: 'Allows user to view API keys',
        },
        update: {
            code: 'api.update',
            displayName: 'Update API Key',
            description: 'Allows user to edit existing API keys',
        },
        delete: {
            code: 'api.delete',
            displayName: 'Delete API Key',
            description: 'Allows user to delete API keys',
        },
    },
};
// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------
/**
 * Returns a flat array of all permission definitions, suitable for
 * seeding/syncing the `permissions` database table.
 */
export function getAllPermissions() {
    return Object.entries(Permissions).flatMap(([category, actions]) => Object.entries(actions).map(([_, def]) => ({
        code: def.code,
        category,
        displayName: def.displayName,
        description: def.description,
    })));
}
//# sourceMappingURL=codes.js.map